'use client';

import * as React from 'react';
import { FiClock } from 'react-icons/fi';

export interface TimePickerProps {
  /**
   * Current time value (HH:MM format)
   */
  value?: string | null;

  /**
   * Callback when time changes
   */
  onChange?: (time: string | null) => void;

  /**
   * Label for the field
   */
  label?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  hint?: string;

  /**
   * Minimum time (HH:MM format)
   */
  min?: string;

  /**
   * Maximum time (HH:MM format)
   */
  max?: string;

  /**
   * Whether the field is disabled
   */
  disabled?: boolean;

  /**
   * Whether the field is required
   */
  required?: boolean;

  /**
   * Step in minutes
   */
  step?: number;

  /**
   * 12 or 24 hour format
   * @default 24
   */
  hourFormat?: 12 | 24;

  /**
   * Container className
   */
  containerClassName?: string;

  /**
   * Input size
   */
  inputSize?: 'sm' | 'md' | 'lg';
}

const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(
  (
    {
      value,
      onChange,
      label,
      placeholder = 'Select time',
      error,
      hint,
      min,
      max,
      disabled,
      required,
      step = 1,
      hourFormat: _hourFormat = 24,
      containerClassName,
      inputSize = 'md',
    },
    ref
  ) => {
    const inputId = React.useId();
    const isInvalid = !!error;

    // Handle time change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (onChange) {
        onChange(newValue || null);
      }
    };

    // Get size classes
    const sizeClasses = {
      sm: 'h-8 text-sm px-3',
      md: 'h-10 text-base px-4',
      lg: 'h-12 text-lg px-5',
    };

    return (
      <div className={containerClassName}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 500,
              marginBottom: '0.5rem',
              color: isInvalid ? '#EF4444' : '#374151',
            }}
          >
            {label}
            {required && <span style={{ color: '#EF4444', marginLeft: '0.25rem' }}>*</span>}
          </label>
        )}

        {/* Time Input Container */}
        <div style={{ position: 'relative' }}>
          <input
            ref={ref}
            id={inputId}
            type="time"
            value={value || ''}
            onChange={handleChange}
            min={min}
            max={max}
            step={step * 60} // Convert minutes to seconds
            disabled={disabled}
            placeholder={placeholder}
            className={sizeClasses[inputSize]}
            style={{
              width: '100%',
              border: `1px solid ${isInvalid ? '#EF4444' : '#D1D5DB'}`,
              borderRadius: '0.375rem',
              backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
              paddingRight: '2.5rem',
              outline: 'none',
              transition: 'border-color 0.15s',
              colorScheme: 'light',
            }}
            onFocus={(e) => {
              if (!isInvalid && !disabled) {
                e.currentTarget.style.borderColor = '#3B82F6';
                e.currentTarget.style.boxShadow = '0 0 0 1px #3B82F6';
              }
            }}
            onBlur={(e) => {
              if (!isInvalid && !disabled) {
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          />

          {/* Clock Icon */}
          <div
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9CA3AF',
              pointerEvents: 'none',
            }}
          >
            <FiClock size={16} />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{ marginTop: '0.25rem' }}>
            <span style={{ color: '#EF4444', fontSize: '0.75rem' }}>{error}</span>
          </div>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <div style={{ marginTop: '0.25rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{hint}</span>
          </div>
        )}
      </div>
    );
  }
);

TimePicker.displayName = 'TimePicker';

export { TimePicker };
